import * as React from 'react';
import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import spacing from 'src/constants/spacing';
import mediaQueries from 'src/constants/responsive';
import stylesheet from 'src/constants/stylesheet';
import PreviewItem from './PreviewItem';
import { useApi } from 'src/@context/Api';
import DropDown, { TOption } from './DropDown';
import * as classNames from 'classnames';
import Loading from './Loading';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            maxWidth: spacing.ContentMaxWidt,
            margin: '0 auto',
            boxSizing: 'content-box',
            [mediaQueries.Mobile]: {
                padding: '0 20px',
            },
            [mediaQueries.AfterMobile]: {
                padding: '0 60px',
            }
        },
        title: {
            ...stylesheet.TitleH1,
            maxWidth: 550,
            margin: '0 auto',
            textAlign: 'center',
            padding: '1em 0 .4em 0',

        },
        subtitle: {
            maxWidth: 550,
            textAlign: 'center',
            margin: '0 auto',
            paddingBottom: '1.5rem',
        },
        panels: {
            [mediaQueries.Mobile]: {},
            [mediaQueries.AfterMobile]: {
                display: 'flex',
                flexWrap: 'wrap',
            },
            [mediaQueries.Tablet]: {
                marginLeft: -spacing.Gutter.Tablet / 2,
                marginRight: -spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                marginLeft: -spacing.Gutter.Laptop / 2,
                marginRight: -spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                marginLeft: -spacing.Gutter.Desktop / 2,
                marginRight: -spacing.Gutter.Desktop / 2,
            },
        },

        panel: {
            boxSizing: 'border-box',
            [mediaQueries.Mobile]: {
                width: '100%',
                marginBottom: spacing.Gutter.Mobile * 2,
            },
            [mediaQueries.Tablet]: {
                paddingLeft: spacing.Gutter.Tablet / 2,
                paddingRight: spacing.Gutter.Tablet / 2,
                marginBottom: spacing.Gutter.Tablet * 2,
            },
            [mediaQueries.TabletPortrait]: {
                width: `${1 / 2 * 100}%`,
            },
            [mediaQueries.TabletLandScape]: {
                width: `${1 / 3 * 100}%`,
            },
            [mediaQueries.AfterTablet]: {
                width: `${1 / 3 * 100}%`,
            },
            [mediaQueries.Laptop]: {
                paddingLeft: spacing.Gutter.Laptop / 2,
                paddingRight: spacing.Gutter.Laptop / 2,
                marginBottom: spacing.Gutter.Laptop * 2,
            },
            [mediaQueries.Desktop]: {
                paddingLeft: spacing.Gutter.Desktop / 2,
                paddingRight: spacing.Gutter.Desktop / 2,
                marginBottom: spacing.Gutter.Desktop * 2,
            },
        },
        
        filterContainer: {
            display: 'flex',
            [mediaQueries.Mobile]: {
                flexDirection: 'column',
                maxWidth: 260,
                margin: '0 auto',
            },
            [mediaQueries.TabletPortrait]: {
                flexDirection: 'column',
                maxWidth: 400,
                margin: '0 auto',
            },
            [mediaQueries.TabletLandScape]: {
                justifyContent: 'center',
                marginBottom: '2rem',
                marginLeft: -spacing.Gutter.Tablet / 2,
                marginRight: -spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.AfterTablet]: {
                justifyContent: 'center',
                marginBottom: '2rem',
            },
            [mediaQueries.Laptop]: {
                justifyContent: 'center',
                marginBottom: '2rem',
                marginLeft: -spacing.Gutter.Laptop / 2,
                marginRight: -spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                justifyContent: 'center',
                marginBottom: '2rem',
                marginLeft: -spacing.Gutter.Desktop / 2,
                marginRight: -spacing.Gutter.Desktop / 2,
            },
        },

        filterElement: {
            [mediaQueries.BeforeTablet]: {
                flex: '0 0 100%',
                width: '100%',
                marginBottom: '1.5rem',
            },
            [mediaQueries.TabletPortrait]: {
                paddingLeft: spacing.Gutter.Tablet / 2,
                paddingRight: spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                paddingLeft: spacing.Gutter.Laptop / 2,
                paddingRight: spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                paddingLeft: spacing.Gutter.Desktop / 2,
                paddingRight: spacing.Gutter.Desktop / 2,
            },
        },

        categoryFilter: {
            [mediaQueries.BeforeTablet]: {
                zIndex: 5
            },
            [mediaQueries.TabletLandScape]: {
                flex: '0 0 200px',
                width: 200,
            },
            [mediaQueries.AfterTablet]: {
                flex: '0 0 200px',
                width: 200,
            },
        },

        yearsFilter: {
            [mediaQueries.BeforeTablet]: {
                zIndex: 3
            },
            [mediaQueries.TabletLandScape]: {
                flex: '0 0 200px',
                width: 200,
            },
            [mediaQueries.AfterTablet]: {
                flex: '0 0 100px',
                width: 100,
            },
        },

        tagsFilter: {
            [mediaQueries.BeforeTablet]: {
                zIndex: 1
            },
            [mediaQueries.TabletLandScape]: {
                flex: '0 0 200px',
                width: 200,
            },
            [mediaQueries.AfterTablet]: {
                flex: '0 0 200px',
                width: 200,
            },
        },
    }));

export type NewsProps = NewsDto;
type Props = NewsProps;

const News: FC<Props> = ({ Title, NewsCategories, UID, Tags }) => {
    const cls = useStyles();

    const itemsToLoad = 9;
    const [hasMore, setHasMore] = useState<boolean>();
    const [count, setCount] = useState(0);

    const { fetchNewsPreviews, app } = useApi();
    const [newsItems, setNewsItems] = useState<PreviewDto[]>([]);
    const [category, setCategory] = useState('');
    const [year, setYear] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const config = app && app.Config;
    const translationsItems = config && config.TranslationItems;
    const categoryTranslation = translationsItems && translationsItems.find(t => t.UID === 'news_category');
    const yearsTranslation = translationsItems && translationsItems.find(t => t.UID === 'news_years');
    const tagsTranslation = translationsItems && translationsItems.find(t => t.UID === 'tags');

    const getPreviews = async () => {
        if (loading) { return }
        setLoading(true);

        const response = await fetchNewsPreviews(
            UID,
            count,
            itemsToLoad,
            category,
            year,
            tags);

        setNewsItems(prevItems => {
            return [...prevItems, ...response.Previews]
        });

        setHasMore(response.More);
        setLoading(false);
    };

    useEffect(() => {
        getPreviews();
    }, [category, year, tags, count]);

    const onTagsChange = (tag: string) => {
        if (tag === "") {
            setTags(['']);
            return;
        }

        if (tags && tags.indexOf(tag) > -1) {

            // const result = tags.filter((t, i) => i !== tags.indexOf(tag));
            const result = tags.splice(tags.indexOf(tag), 1);

            // TODO: Why does this not trigger useEffect? Something about mutable and imutable object?
            // tags.splice(tags.indexOf(tag), 1);

            setTags(result);
        } else {
            setTags([...tags, tag])
        }
    }

    const firstYear = new Date('2010-01-01').getFullYear();
    let now = new Date().getFullYear();
    const years: TOption[] = [];

    while (now >= firstYear) {
        const result = {
            "title": now.toString(),
            "value": now.toString()
        };

        years.push(result);
        now--;
    };

    const options: IntersectionObserverInit = { threshold: 1 }
    const observer = useRef<IntersectionObserver>();
    const lastItemLoaded = useCallback((item) => {
        if (loading) { return }

        if (observer.current) { observer.current.disconnect() }

        observer.current = new IntersectionObserver(items => {
            if (items[0].isIntersecting && hasMore) {
                setCount(prevCount => prevCount + itemsToLoad);
                // getPreviews();
            }
        }, options)

        if (item) {
            observer.current.observe(item);
        }
    }, [loading]);

    return (
        <section className={cls.root}>
            {Title &&
                <h1 className={cls.title}>{Title}</h1>
            }

            <div className={cls.filterContainer}>
                {NewsCategories &&
                    <div className={classNames(cls.filterElement, cls.categoryFilter)}>
                        <DropDown
                            title={categoryTranslation && categoryTranslation.Translation || ''}
                            options={NewsCategories.map(t => ({ title: t.Title, value: t.ObjectId }))}
                            isTags={false}
                            // tslint:disable-next-line:jsx-no-lambda
                            onChanged={(item) => {
                                setCategory(item && item.value || '');
                                setCount(0);
                                setNewsItems([]);
                            }}
                        />
                    </div>
                }
                {
                    years &&
                    <div className={classNames(cls.filterElement, cls.yearsFilter)}>
                        <DropDown
                            title={yearsTranslation && yearsTranslation.Translation || ''}
                            options={years}
                            isTags={false}
                            // tslint:disable-next-line:jsx-no-lambda
                            onChanged={(item) => {
                                setYear(item && item.value || '');
                                setCount(0);
                                setNewsItems([]);
                            }}
                        />
                    </div>
                }
                {
                    Tags &&
                    <div className={classNames(cls.filterElement, cls.tagsFilter)}>
                        <DropDown
                            title={tagsTranslation && tagsTranslation.Translation || ''}
                            options={Tags.map(t => ({ title: t.Title, value: t.ObjectId }))}
                            isTags={true}
                            // tslint:disable-next-line:jsx-no-lambda
                            onChanged={(item) => {
                                onTagsChange(item && item.value || '');
                                setCount(0);
                                setNewsItems([]);
                            }}
                        />
                    </div>
                }
            </div>
            {newsItems &&
                <article className={cls.panels}>
                    {newsItems.map((newsItem, i) => {
                        if (newsItems.length === i + 1) {
                            return (<div
                                key={`newItem-${newsItem}-${i}`}
                                className={cls.panel}
                                ref={lastItemLoaded}
                            >
                                <PreviewItem previewItem={newsItem as IDto} />
                            </div>);
                        } else {
                            return (
                                <div
                                    key={`newItem-${newsItem}-${i}`}
                                    className={cls.panel}
                                >
                                    <PreviewItem previewItem={newsItem as IDto} />
                                </div>
                            );
                        }
                    })}
                </article>
            }
            {loading && <Loading />}
        </section>
    );
}

export default News;


// WEBPACK FOOTER //
// ./src/Components/News.tsx